import type { StyleProp, ViewStyle } from 'react-native';
import type { ReactNode } from 'react';
import type { AccentKey } from '../theme/accents';

/** Variantes visuelles des composants de base (cf. docs/02_DESIGN_SYSTEM.md). */
export type ButtonVariant = 'primary' | 'success' | 'secondary' | 'dark';
export type CardVariant = 'default' | 'feature' | 'dark';
export type ChipVariant =
  | 'blueSoft'
  | 'greenSoft'
  | 'limeSoft'
  | 'successSoft'
  | 'warningSoft'
  | 'infoSoft';

export interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: ButtonVariant;
  withArrow?: boolean;
  loading?: boolean;
  disabled?: boolean;
  testID?: string;
}

export interface CardProps {
  children: ReactNode;
  variant?: CardVariant;
  // Mise en avant : bordure colorée + léger fond teinté dérivés de l'accent (élément principal).
  emphasis?: boolean;
  accent?: AccentKey;
  /** Mise en page seulement — la grille des listes s'en sert pour poser la largeur d'une cellule. */
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export interface ChipProps {
  label: string;
  variant?: ChipVariant;
  testID?: string;
}

export interface ScoreRingProps {
  score: number; // 0–100 (clampé)
  size?: number;
  // 'degrade' : anneau plus épais au dégradé, chiffre plus grand (écrans refaits).
  // 'marque' : dégradé de la marque (cyan, violet, corail) quel que soit le score, chiffre petit suivi de « % ».
  variante?: 'classique' | 'degrade' | 'marque';
  // 'sombre' : chiffre blanc et piste claire, pour un anneau posé sur fond bleu nuit.
  surFond?: 'clair' | 'sombre';
  // Texte sous le chiffre (« sur 100 »).
  legende?: string;
  testID?: string;
}

/** Onglet de la BottomNav (le FAB central IA est géré à part). */
/**
 * Langue des contenus PRODUITS par l'IA. Le marché reste la France : un document en anglais reste
 * destiné à un recruteur français, et les règles de fond ne varient pas (cf.
 * supabase/functions/_shared/outputLanguage.ts).
 *
 * La lettre de motivation garde son propre paramètre `langue` ('francais' | 'anglais'), hérité du
 * document de référence et déjà câblé dans son prompt : on le RÉUTILISE plutôt que d'en ajouter un
 * second qui dirait la même chose.
 */
export type OutputLanguage = 'fr' | 'en';

// Type de contrat recherché, choisi à la création d'un CV (facultatif, lot 9g).
export const CV_CONTRACT_TYPES = ['alternance', 'stage', 'cdi', 'cdd'] as const;
export type CvContractType = (typeof CV_CONTRACT_TYPES)[number];

export const OUTPUT_LANGUAGES: { value: OutputLanguage; label: string }[] = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'Anglais' },
];

export type TabKey = 'cvs' | 'offers' | 'news' | 'profile';

export interface BottomNavProps {
  activeKey: TabKey;
  onTabPress: (key: TabKey) => void;
  onFabPress: () => void;
  testID?: string;
}

/**
 * ARTICLES DU FIL NEWS.
 *
 * TOUT EST OPTIONNEL SAUF `id`, et ce n'est pas de la prudence décorative : ces objets viennent
 * d'une API externe alimentée par un CMS où les champs se remplissent à la main. Un type qui
 * déclare `title: string` ne fait pas apparaître le titre à l'exécution — il fait seulement taire
 * le compilateur là où il aurait fallu se méfier. Le service normalise et l'affichage dégrade.
 *
 * `id` est la SEULE exigence : il sert de clé React et de clé de favori. Un article sans id est
 * écarté à la lecture (cf. `src/services/news.ts`), faute de quoi il produirait une clé dupliquée
 * et un favori impossible à retirer.
 *
 * Champs de l'API délibérément ABSENTS de ce type, pour ce lot : `author` (on affiche l'éditeur),
 * `sponsored`, `cta`, `cover` (ancien dégradé CSS) et `image` (doublon de `coverImage.url`).
 * Les ignorer dans le type, et pas seulement dans le JSX, évite qu'ils s'affichent par accident.
 */
export interface NewsPublisher {
  id?: string;
  name?: string;
  logoUrl?: string;
  logoWidth?: number;
  logoHeight?: number;
  verified?: boolean;
}

export interface NewsCoverImage {
  url?: string;
  width?: number;
  height?: number;
  alt?: string;
}

export interface NewsArticle {
  /** Identifiant Sanity, immuable. JAMAIS le slug, qui dérive du titre et change avec lui. */
  id: string;
  slug?: string;
  title?: string;
  excerpt?: string;
  category?: string;
  /** « YYYY-MM-DD », sans heure ni fuseau. Voir `src/utils/relativeDate.ts`. */
  date?: string;
  readingMinutes?: number;
  publisher?: NewsPublisher;
  coverImage?: NewsCoverImage;
}

/** Une page du fil, telle que le service la rend après normalisation de l'enveloppe. */
export interface NewsPage {
  articles: NewsArticle[];
  hasNextPage: boolean;
  /** `string | null`, jamais `undefined` : l'API renvoie explicitement `null` en fin de fil. */
  nextCursor: string | null;
}

/** Plans d'abonnement (cf. docs/01_PRODUCT_SPEC.md). */
export type Plan = 'free' | 'plus' | 'pro' | 'school';

/** Templates visuels du CV (cf. src/theme/templates.ts). */
export type CvTemplate = 'mono' | 'accent';

/** Clés de feature soumises au gating serveur (cf. docs/04_DATA_MODEL.md). */
export type FeatureKey =
  | 'cv_score'
  | 'linkedin_score'
  | 'cv_create'
  | 'cv_import'
  | 'cv_improve'
  | 'section_rewrite'
  | 'interview_sim'
  | 'cover_letter'
  | 'cover_letter_analyse'
  | 'cover_letter_improve'
  | 'offre_dossier'
  | 'export';

// ───────────── Entrées média ─────────────
export type ImageMediaType = 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';

export interface ImageInput {
  media_type: ImageMediaType;
  data: string; // base64
}

// ───────────── Résultats IA (sortie des Edge Functions) ─────────────
export interface LinkedinPriority {
  title: string;
  why: string;
  fix: string;
}

export interface LinkedinExperienceRewrite {
  original: string;
  suggestion: string;
}
export interface LinkedinTitles {
  recommended: 'v1' | 'v2' | 'v3';
  why: string;
  v1: string;
  v2: string;
  v3: string;
}
export interface LinkedinAbout {
  short: string;
  full: string;
}
export interface LinkedinSkills {
  pinned: string[];
  complementary: string[];
}
export interface LinkedinSearch {
  titles: string[];
  contracts: string[];
  locations: string[];
  sectors: string[];
  availability: string;
  note: string;
}
export interface LinkedinExpress {
  photo: string;
  banner: string;
  url: string;
  education: string;
  recommendation: { who: string; message: string };
}
export interface LinkedinQualityCheck {
  erreurs_corrigees: QualityCheckError[];
  elements_supprimes: string[];
  keywords_added: string[];
  a_valider: string[];
  competences_non_demontrees: string[];
  langues_retirees: string[];
  recommandations: string[];
}
// Kit d'optimisation LinkedIn (prêt à coller). OPTIONNEL : mode captures partiel / ancien audit absent.
export interface LinkedinOptimization {
  titles: LinkedinTitles;
  about: LinkedinAbout;
  experiences: LinkedinExperienceRewrite[];
  skills: LinkedinSkills;
  search: LinkedinSearch;
  express: LinkedinExpress;
  quality_check: LinkedinQualityCheck;
}
// Note par section, de 0 à 100. Une clé absente = section non évaluée : l'écran n'affiche rien pour elle.
export interface LinkedinSubScores {
  title?: number;
  about?: number;
  experiences?: number;
}
export interface LinkedinAudit {
  score: number;
  sub_scores?: LinkedinSubScores;
  priorities: LinkedinPriority[];
  keywords_missing: string[];
  optimization?: LinkedinOptimization;
}

export interface CvExperience {
  role: string;
  company: string;
  start_date: string; // ex. « Janv. 2024 » ; '' si absent
  end_date: string; // ex. « En cours » / « Juin 2025 » ; '' si absent
  bullets: string[];
}

export interface CvEducation {
  degree: string;
  school: string;
  date: string;
}

// Projet personnel / associatif / side-project (distinct des expériences pro).
export interface CvProject {
  name: string;
  date: string;
  bullets: string[];
}

export interface QualityCheckError {
  numero: number;
  erreur: string;
  explication: string;
  correction: string;
}
// Bloc « Ce qui a été amélioré » (optionnel, pédagogique). Rétrocompatible : un CV sans ce champ
// reste valide (les CV déjà générés en base ne cassent pas).
export interface QualityCheck {
  erreurs_corrigees: QualityCheckError[];
  elements_supprimes: string[];
  a_valider: string[];
  langues_retirees: string[];
  competences_non_demontrees: string[];
  recommandations: string[];
}
export interface GeneratedCv {
  headline: string; // titre normalisé par l'IA (casse/orthographe pro) du poste visé
  experiences: CvExperience[];
  projects: CvProject[];
  education: CvEducation[];
  skills: string[];
  languages: string[];
  interests: string[];
  ats_score: number;
  advice: string[];
  quality_check?: QualityCheck; // optionnel (contrôle qualité pédagogique)
  // Vrai quand ce CV n'a pas été noté (optimisé par modifications) : `ats_score` vaut alors 0 par
  // construction et ne doit ni s'afficher ni s'enregistrer comme un vrai 0 (cf. `scoreAEnregistrer`).
  score_inconnu?: boolean;
}

// Optimisation par modifications (lot 9c) : même forme que `CvChanges` côté serveur.
export type CvChangeTag = 'impact' | 'action' | 'keywords' | 'clarity' | 'title';

export interface CvChange {
  id: string;
  target: 'headline' | 'bullet';
  section?: 'experiences' | 'projects';
  item?: number;
  bullet?: number;
  tag: CvChangeTag;
  before: string;
  after: string;
  reason: string;
}

export interface CvChanges {
  cv: GeneratedCv;
  changes: CvChange[];
}

// Amélioration d'une lettre par modifications (lot 9c2) : même forme que `LetterChanges` côté serveur.
export type LetterChangeTag = 'hook' | 'proof' | 'keywords' | 'clarity' | 'closing' | 'subject';

export interface LetterChange {
  id: string;
  target: 'subject' | 'paragraph';
  /** Position du paragraphe dans la lettre envoyée (à partir de 0), absente pour l'objet. */
  paragraph?: number;
  tag: LetterChangeTag;
  /** Lu par le serveur dans la lettre envoyée : sert à vérifier qu'on peut appliquer. */
  before: string;
  after: string;
  reason: string;
}

export interface LetterChanges {
  changes: LetterChange[];
}

/** Ce que l'analyse d'une lettre transmet à son amélioration : de quoi orienter, rien de plus. */
export interface LetterImproveDiagnostic {
  priorites: string[];
  erreurs: { extrait: string; correction: string }[];
  motsClesManquants: string[];
  pointsForts: string[];
  voixCandidat: string[];
  incoherences: string[];
}

export interface RewrittenSection {
  section: string;
  before: string;
  after: string;
}

// Quatre notes de 0 à 100 dont le score ATS est la moyenne pondérée (calculée par le serveur).
export interface CvCriteria {
  keywords: number;
  layout: number;
  impact: number;
  readability: number;
}

export interface CvScore {
  ats_score: number;
  // Absent quand le serveur n'a pas pu noter les quatre critères : l'écran n'en montre alors aucun.
  criteria?: CvCriteria;
  advice: string[];
  // Présents UNIQUEMENT quand une fiche de poste est fournie (notation relative à l'offre).
  match_score?: number; // correspondance CV ↔ offre (0-100)
  strengths?: string[]; // points forts vis-à-vis du poste
  gaps?: string[]; // écarts / manques vis-à-vis du poste
}

// ───────────── Simulateur d'entretien (claude-interview) ─────────────
// Posture de l'IA : recruteur RH (fit) ou manager opérationnel (métier/technique).
export type InterviewRole = 'recruiter' | 'manager';

export interface InterviewMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface InterviewFeedback {
  strengths: string[];
  improvements: string[];
  tips: string[];
  overall: string;
}

// ───────────── Saisie structurée du formulaire de création de CV ─────────────
export interface MonthYear {
  month: string; // '' | '1'..'12'
  year: string; // '' | 'AAAA'
}

export interface ExperienceEntry {
  role: string;
  company: string;
  start: MonthYear;
  end: MonthYear;
  current: boolean; // « Poste actuel » → remplace la date de fin
  description: string;
}

export interface ProjectEntry {
  name: string;
  date: MonthYear;
  current: boolean;
  description: string;
}

export interface EducationEntry {
  degree: string;
  school: string;
  date: MonthYear;
  current: boolean;
}

// Saisie structurée des langues dans le flow de création de CV.
export type CefrLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2' | 'Natif';

export interface LanguageEntry {
  name: string;
  level: CefrLevel;
  diploma?: string;
}

/** Profil utilisateur (table `profiles`, cf. docs/04_DATA_MODEL.md). */
// Objet de recherche (sélecteur du profil) — clé persistée en base (cf. config/searchTarget).
export type SearchTarget = 'stage' | 'alternance' | 'contrat';

export interface Profile {
  id: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null; // email de contact (≠ email de connexion)
  phone: string | null;
  address: string | null;
  avatar_url: string | null; // photo importée de l'avatar (URL Storage) ; prioritaire sur avatar_key
  banner_url: string | null; // photo importée de la bannière (URL Storage) ; prioritaire sur banner_key
  banner_key: string | null; // clé du fond coloré prédéfini de la bannière (cf. theme/profileBackgrounds)
  avatar_key: string | null; // clé du fond coloré prédéfini de l'avatar
  city: string | null;
  education: string | null;
  availability: string | null;
  search_target: SearchTarget | null; // objet de recherche : stage | alternance | contrat
  letter_profil?: LetterProfil | null; // profil retenu pour la lettre (lot 9f) ; absent tant que la migration 0086 n'est pas appliquée
  letter_type?: LetterType | null; // type de candidature retenu pour la lettre (lot 9f)
  linkedin_url: string | null;
  birth_date: string | null; // date de naissance ISO 'YYYY-MM-DD' (KPI âge). Saisie post-auth, optionnelle.
  email_domain: string | null; // domaine de l'email de connexion, dérivé serveur (KPI établissement). Lecture seule.
  terms_accepted_at: string | null; // horodatage ISO d'acceptation des CGU (preuve de consentement).
  terms_version: string | null; // version des CGU acceptée.
  locale: string;
}

// ───────────── Lettre de motivation (claude-letter) ─────────────
// Le champ `analyse` du modèle est retiré CÔTÉ SERVEUR : il n'existe pas dans ce type, donc il ne
// peut ni être affiché ni être persisté.
export const LETTER_PROFILS = [
  'collegien',
  'lyceen',
  'etudiant_jeune_diplome',
  'actif_confirme',
  'manager_cadre_dirigeant',
  'reconversion',
] as const;
export type LetterProfil = (typeof LETTER_PROFILS)[number];

export const LETTER_TYPES = ['reponse_offre', 'candidature_spontanee', 'mobilite_interne'] as const;
export type LetterType = (typeof LETTER_TYPES)[number];

export const LETTER_TONS = [
  'classique',
  'chaleureux',
  'direct',
  'entrepreneurial',
  'institutionnel',
  'executif',
] as const;
export type LetterTon = (typeof LETTER_TONS)[number];

// Vocabulaire hérité du prompt de la lettre, distinct de OutputLanguage ('fr' | 'en') : ces
// valeurs partent telles quelles dans <parametres>, où « francais » se lit mieux que « fr ».
export type LetterLangue = 'francais' | 'anglais';

export interface CoverLetterParams {
  profil: LetterProfil;
  typeCandidature: LetterType;
  ton: LetterTon;
  langue: LetterLangue;
  destinataireNom: string;
  destinataireFonction: string;
  organisation: string;
  metier: string;
  ville: string;
  emailAccompagnement: boolean;
}

export interface CoverLetter {
  objet: string;
  candidat: { nom_complet: string; ville_ou_mobilite: string; telephone: string; email: string };
  destinataire: { organisation: string; service_ou_fondation: string; nom: string; fonction: string; ville: string };
  lieu_date: string;
  lettre: {
    formule_appel: string;
    paragraphes: string[];
    formule_politesse: string;
    signature: string;
  };
  email_accompagnement: { objet: string; corps: string } | null;
  informations_a_confirmer: string[];
  nombre_mots: number; // recompté par le serveur, jamais rapporté par le modèle
  interligne: number; // dérivé du nombre de mots, pilote le rendu PDF
  /**
   * Vrai quand le texte a été TRANSCRIT par le modèle depuis un PDF importé, et n'est donc pas
   * l'original. Porté par le contenu jsonb plutôt que par une colonne : aucune migration, et
   * l'information suit la lettre partout où elle est relue.
   */
  texte_reconstitue?: boolean;
}

export interface SavedCoverLetter {
  id: string;
  title: string;
  created_at: string;
  content: CoverLetter;
  output_language: OutputLanguage;
}

// ───────────── Analyse d'une lettre (lot 2) ─────────────

export type LetterVerdict =
  | 'a_refaire'
  | 'a_retravailler'
  | 'envoyable_apres_corrections'
  | 'solide'
  | 'excellente';

export type LetterNiveau = 'insuffisant' | 'fragile' | 'solide' | 'excellent';
export type LetterGravite = 'majeure' | 'moyenne' | 'mineure';

export interface LetterSousScore {
  critere: string;
  niveau: LetterNiveau;
  poids: number;
  points: number;
  justification: string;
}

export interface LetterErreur {
  numero: number;
  erreur: string;
  critere: string;
  gravite: LetterGravite;
  extrait: string;
  explication_pedagogique: string;
  correction_suggeree: string;
}

/**
 * Diagnostic complet renvoyé par claude-letter en mode analyse.
 *
 * `score` et `verdict` sont RECONSTRUITS côté serveur : le modèle ne les annonce pas, il ne
 * connaît ni les pondérations ni les plafonds. `voix_candidat` et `questions` ne s'affichent pas
 * dans ce lot mais sont conservés — ils sont l'entrée obligatoire de l'optimisation (lot 3).
 */
export interface LetterAnalysis {
  score: number;
  verdict: LetterVerdict;
  plafond_applique: string | null;
  resume: string;
  sous_scores: LetterSousScore[];
  erreurs_detectees: LetterErreur[];
  nombre_fautes: number;
  adequation_poste: {
    exigences_couvertes: string[];
    exigences_non_adressees: string[];
    mots_cles_manquants: string[];
  };
  points_forts: string[];
  voix_candidat: string[];
  priorites: string[];
  questions: Array<{
    id: number;
    question: string;
    pourquoi: string;
    exemple_reponse: string;
    type_attendu: string;
  }>;
  incoherences_avec_cv: Array<{ affirmation: string; constat: string; bloquant: boolean }>;
  /** Le CV a-t-il été croisé ? L'écran le dit : sans CV, aucun fait n'est vérifié. */
  cv_croise: boolean;
  profil: string;
  empreinte: string;
  /**
   * Texte de la lettre analysée, renvoyé par le serveur pour que le client puisse l'enregistrer
   * dans « Mes lettres » — le client ne l'a pas quand la lettre arrive en document importé.
   * OPTIONNELS : les analyses produites avant l'import de fichier n'en portent pas.
   */
  lettre_texte?: string;
  /** Vrai si ce texte est une transcription d'un PDF par le modèle, et non l'original. */
  texte_reconstitue?: boolean;
}

/** Analyse relue depuis la base, avec de quoi savoir si elle porte encore sur le texte courant. */
export interface StoredLetterAnalysis {
  cover_letter_id: string;
  created_at: string;
  empreinte: string;
  diagnostic: LetterAnalysis;
}
